import { useTranslation } from 'react-i18next'
import { FormControl } from '~/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { USE_DEFAULT, useDefaultLabel } from '~/lib/inheritable'

/**
 * The select of an inheritable setting (`~/lib/inheritable`): "Use default
 * (<what it inherits>)" first (value `USE_DEFAULT`, the API's null), then
 * the setting's own values. Its trigger sits in a `FormControl`, so it is for
 * a `FormField`'s render only; the field's form name must be no key of the
 * record (`<key>Choice`), or Refine writes the record's null over
 * `USE_DEFAULT`.
 */
export function InheritableSelect<Value extends string>({
  value,
  onChange,
  options,
  inheritedLabel,
  disabled,
  'aria-label': ariaLabel,
}: {
  value: string
  onChange: (value: string) => void
  options: readonly { value: Value; label: string }[]
  /** what Use default resolves to, by name; undefined while it loads */
  inheritedLabel?: string | null
  disabled?: boolean
  'aria-label': string
}) {
  const { t } = useTranslation()
  return (
    <Select
      value={value}
      disabled={disabled}
      // Radix reports '' while the value has no item yet; that would wipe
      // the record's value
      onValueChange={(next) => {
        if (next !== '') {
          onChange(next)
        }
      }}
    >
      <FormControl>
        <SelectTrigger aria-label={ariaLabel}>
          <SelectValue />
        </SelectTrigger>
      </FormControl>
      <SelectContent>
        <SelectItem value={USE_DEFAULT}>
          {useDefaultLabel(t, inheritedLabel)}
        </SelectItem>
        {options.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
